import { getFunctionUrl } from "../config";
import { getAuthToken } from "./auth";

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

type PlanningMethod = "GET" | "POST" | "PATCH";

export class PlanningApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "PlanningApiError";
    this.code = code;
    this.status = status;
  }
}

export interface PlanningProject {
  dbvdwProjectId: string;
  displayName: string;
  projectCode: string;
  canWrite: boolean;
  [key: string]: unknown;
}

export interface PlanningSourceProject {
  id: number;
  identifier: string;
  name: string;
  active: boolean;
}

export interface PlanningType {
  id: number;
  name: string;
  milestone: boolean;
}

export interface PlanningStatus {
  id: number | null;
  name: string | null;
}

export interface PlanningAssignee {
  id: number;
  name: string | null;
}

export interface PlanningWorkPackage {
  id: number;
  projectId: number;
  subject: string;
  type: PlanningType;
  status: PlanningStatus;
  startDate: string | null;
  dueDate: string | null;
  duration: string | null;
  scheduleManually: boolean;
  lockVersion: number;
  parentId: number | null;
  childIds: number[];
  assignee: PlanningAssignee | null;
}

export type PlanningDependencyType =
  | "relates"
  | "duplicates"
  | "blocks"
  | "precedes"
  | "includes"
  | "requires";

export interface PlanningDependency {
  id: number;
  type: PlanningDependencyType;
  fromWorkPackageId: number;
  toWorkPackageId: number;
  lagDays: number;
}

export interface PlanningProjectPlan {
  project: PlanningSourceProject;
  types: PlanningType[];
  workPackages: PlanningWorkPackage[];
  dependencies: PlanningDependency[];
}

export interface PlanningWindowReadModel {
  project: PlanningSourceProject;
  startDate: string;
  endDate: string;
  workPackages: PlanningWorkPackage[];
  dependencies: PlanningDependency[];
  unscheduledCount: number;
}

function requireProjectId(value: string): string {
  if (!UUID_SHAPE.test(value)) {
    throw new PlanningApiError("invalid_request", "Ongeldig project.", 400);
  }
  return value;
}

function requireIsoDate(value: string, field: string): string {
  if (!ISO_DATE.test(value)) {
    throw new PlanningApiError(
      "invalid_request",
      `${field} moet YYYY-MM-DD zijn.`,
      400,
    );
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  if (
    Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value
  ) {
    throw new PlanningApiError(
      "invalid_request",
      `${field} moet YYYY-MM-DD zijn.`,
      400,
    );
  }
  return value;
}

function optionalIsoDate(
  value: string | null | undefined,
  field: string,
): string | null {
  if (!value) return null;
  return requireIsoDate(value, field);
}

function positiveInteger(value: number, field: string): number {
  if (!Number.isInteger(value) || value <= 0) {
    throw new PlanningApiError(
      "invalid_request",
      `${field} moet een positief geheel getal zijn.`,
      400,
    );
  }
  return value;
}

function nonNegativeInteger(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new PlanningApiError(
      "invalid_request",
      `${field} moet nul of hoger zijn.`,
      400,
    );
  }
  return value;
}

function requireSubject(value: string): string {
  const subject = value.trim();
  if (!subject || subject.length > 255) {
    throw new PlanningApiError(
      "invalid_request",
      "Omschrijving is verplicht en mag maximaal 255 tekens bevatten.",
      400,
    );
  }
  return subject;
}

function requireDateOrder(
  startDate: string | null,
  dueDate: string | null,
): void {
  if (startDate && dueDate && startDate > dueDate) {
    throw new PlanningApiError(
      "invalid_request",
      "Einddatum mag niet vóór startdatum liggen.",
      400,
    );
  }
}

function responseObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

async function request<T>(
  path: string,
  { method = "GET", body = null, signal }: {
    method?: PlanningMethod;
    body?: Record<string, unknown> | null;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  if (!path.startsWith("/") || path.startsWith("//")) {
    throw new PlanningApiError("invalid_request", "Ongeldig planningpad.", 400);
  }
  const token = await getAuthToken();
  if (!token) throw new PlanningApiError("unauthorized", "Niet ingelogd.", 401);

  const headers = new Headers({ Authorization: `Bearer ${token}` });
  const serialized = body === null ? undefined : JSON.stringify(body);
  if (serialized !== undefined) headers.set("Content-Type", "application/json");

  let response: Response;
  try {
    response = await fetch(getFunctionUrl("dbvdw-planning", path), {
      method,
      headers,
      body: serialized,
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new PlanningApiError(
      "unreachable",
      method === "GET"
        ? "Planningservice is niet bereikbaar."
        : "Planningwijziging heeft geen bevestigd antwoord. Ververs de planning vóór je opnieuw probeert.",
      503,
    );
  }

  const data: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const payload = responseObject(data);
    throw new PlanningApiError(
      typeof payload.error === "string" ? payload.error : "planning_error",
      typeof payload.message === "string"
        ? payload.message
        : `Planningverzoek mislukt (HTTP ${response.status}).`,
      response.status,
    );
  }
  if (response.status !== 204 && data === null) {
    throw new PlanningApiError(
      "invalid_response",
      "Planningservice gaf geen geldig antwoord.",
      502,
    );
  }
  return data as T;
}

export const planningApi = {
  listProjects: (signal?: AbortSignal) => request<PlanningProject[]>("/projects", { signal }),
  getPlan: (projectId: string, signal?: AbortSignal) =>
    request<PlanningProjectPlan>(
      `/projects/${requireProjectId(projectId)}/plan`,
      { signal },
    ),
  getWeek: (projectId: string, weekStart: string, signal?: AbortSignal) => {
    const params = new URLSearchParams({
      week_start: requireIsoDate(weekStart, "week_start"),
    });
    return request<PlanningWindowReadModel>(
      `/projects/${requireProjectId(projectId)}/week?${params}`,
      {
        signal,
      },
    );
  },
  getLookahead: (
    projectId: string,
    startDate: string,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams({
      start_date: requireIsoDate(startDate, "start_date"),
    });
    return request<PlanningWindowReadModel>(
      `/projects/${requireProjectId(projectId)}/lookahead?${params}`,
      { signal },
    );
  },
  createWorkPackage: (
    projectId: string,
    input: {
      subject: string;
      typeId: number;
      parentId?: number | string | null;
      startDate?: string | null;
      dueDate?: string | null;
    },
  ) => {
    const startDate = optionalIsoDate(input.startDate, "startdatum");
    const dueDate = optionalIsoDate(input.dueDate, "einddatum");
    requireDateOrder(startDate, dueDate);
    const parentId = input.parentId == null || input.parentId === ""
      ? null
      : positiveInteger(Number(input.parentId), "parent_id");
    return request<unknown>(
      `/projects/${requireProjectId(projectId)}/work-packages`,
      {
        method: "POST",
        body: {
          subject: requireSubject(input.subject),
          type_id: positiveInteger(Number(input.typeId), "type_id"),
          start_date: startDate,
          due_date: dueDate,
          parent_id: parentId,
        },
      },
    );
  },
  updateSchedule: (
    projectId: string,
    workPackageId: number,
    input: { expectedLockVersion: number; startDate: string; dueDate: string },
  ) => {
    const startDate = requireIsoDate(input.startDate, "startdatum");
    const dueDate = requireIsoDate(input.dueDate, "einddatum");
    requireDateOrder(startDate, dueDate);
    return request<unknown>(
      `/projects/${requireProjectId(projectId)}/work-packages/${
        positiveInteger(workPackageId, "work_package_id")
      }/schedule`,
      {
        method: "PATCH",
        body: {
          expected_lock_version: nonNegativeInteger(
            input.expectedLockVersion,
            "expected_lock_version",
          ),
          start_date: startDate,
          due_date: dueDate,
        },
      },
    );
  },
  createDependency: (
    projectId: string,
    input: {
      fromWorkPackageId: number;
      toWorkPackageId: number;
      lagDays: number;
    },
  ) => {
    const fromId = positiveInteger(
      input.fromWorkPackageId,
      "from_work_package_id",
    );
    const toId = positiveInteger(input.toWorkPackageId, "to_work_package_id");
    if (fromId === toId) {
      throw new PlanningApiError(
        "invalid_request",
        "Een planningitem kan niet van zichzelf afhankelijk zijn.",
        400,
      );
    }
    return request<unknown>(
      `/projects/${requireProjectId(projectId)}/dependencies`,
      {
        method: "POST",
        body: {
          from_work_package_id: fromId,
          to_work_package_id: toId,
          lag_days: nonNegativeInteger(input.lagDays, "lag_days"),
        },
      },
    );
  },
};
